import { EventEmitter } from '@angular/core';
import { IBAN_BLOCKS } from '../iban-input-field/iban/iban-blocks';
import { IBANCulture } from '../iban-input-field/iban/iban-culture';
import { ibanValidator } from '../iban-input-field/iban/iban-validator';
import { BankDe } from './bank-de';
// import at from '../json/at.json';
// import be from '../json/be.json';
import de from '../json/de.json';
// import es from '../json/es.json';
// import fr from '../json/fr.json';
// import lu from '../json/lu.json';
// import nl from '../json/nl.json';

// Check https://wise.com/gb/iban/

export class IBANDetail {
  public validityChanged: EventEmitter<boolean> = new EventEmitter();

  private _iban: string;
  public get iban(): string {
    return this._iban;
  }

  public set iban(val: string) {
    this._iban = val;
    this.validate();
  }

  private _printFormat: Array<number>;
  public get printFormat(): Array<number> {
    return this._printFormat;
  }

  private _ibanLength: number;
  public get ibanLength(): number {
    return this._ibanLength;
  }

  private _countryCode: string;
  public get countryCode(): string {
    return this._countryCode;
  }

  private _checkDigit?: number;
  public get checkDigit(): number | undefined {
    return this._checkDigit;
  }

  // BLZ
  private _bankCode?: string;
  public get bankCode(): string | undefined {
    return this._bankCode;
  }

  // Kontonummer
  private _accountNumber?: string;
  public get accountNumber(): string | undefined {
    return this._accountNumber;
  }

  // Name of the bank
  private _bankName?: string;
  public get bankName(): string | undefined {
    return this._bankName;
  }

  // BIC
  private _swiftCode?: string;
  public get swiftCode(): string | undefined {
    return this._swiftCode;
  }

  private _isValid?: boolean;
  public get isValid(): boolean | undefined {
    return this._isValid;
  }

  constructor(countryCode: IBANCulture, iban: string | undefined) {
    this._countryCode = IBANCulture[countryCode].toString();
    this._iban = iban ? iban.replace(/\s+/g, '') : '';
    this.refreshCountryFormat();

    if (this._iban.trim().length !== 0) {
      this.validate();
    }
  }

  private validate() {
    this.refreshCountryFormat();

    // Check if length fit to format length
    const replaced = this._iban.replace(/\s+/g, '');
    const cleaned = replaced.replace(/[_]/g, '');

    this._isValid = false;
    this._checkDigit = undefined;

    if (
      cleaned.length !== this._ibanLength ||
      ibanValidator(cleaned) !== null
    ) {
      this.validityChanged.emit(this._isValid);
      return;
    }

    this._checkDigit = Number(this.calculateIbanCheckDigits(cleaned));
    this._bankCode = this.getBankCode(cleaned);
    this._accountNumber = this.getAccountNumber(cleaned);
    this._bankName = this.getBankName();
    this._swiftCode = this.getSWIFTCode();
    this._isValid = true;

    this.validityChanged.emit(this._isValid);
  }

  private refreshCountryFormat() {
    const detectedCountryCode = this.detectCountryCode(this._iban);

    if (detectedCountryCode) {
      this._countryCode = detectedCountryCode;
    }

    this._printFormat = this.getFormat();
    this._ibanLength = this._printFormat.reduce((sum, p) => sum + p);
  }

  private detectCountryCode(iban: string): string | undefined {
    const countryCode = iban.replace(/\s+/g, '').slice(0, 2).toUpperCase();

    if (!countryCode) {
      return undefined;
    }

    return Object.prototype.hasOwnProperty.call(IBANCulture, countryCode)
      ? countryCode
      : undefined;
  }

  /**
   * Get the iban format for current culture
   * @param culture culture
   * @returns Array of block sizes
   */
  private getFormat(): Array<number> {
    const culture: IBANCulture =
      IBANCulture[this._countryCode as keyof typeof IBANCulture];

    switch (culture) {
      case IBANCulture.AT:
        return IBAN_BLOCKS.AT;
      case IBANCulture.BE:
        return IBAN_BLOCKS.BE;
      case IBANCulture.BG:
        return IBAN_BLOCKS.BG;
      case IBANCulture.CH:
        return IBAN_BLOCKS.CH;
      case IBANCulture.CY:
        return IBAN_BLOCKS.CY;
      case IBANCulture.CZ:
        return IBAN_BLOCKS.CZ;
      case IBANCulture.DE:
        return IBAN_BLOCKS.DE;
      case IBANCulture.DK:
        return IBAN_BLOCKS.DK;
      case IBANCulture.EE:
        return IBAN_BLOCKS.EE;
      case IBANCulture.ES:
        return IBAN_BLOCKS.ES;
      case IBANCulture.FI:
        return IBAN_BLOCKS.FI;
      case IBANCulture.FR:
        return IBAN_BLOCKS.FR;
      case IBANCulture.GB:
        return IBAN_BLOCKS.GB;
      case IBANCulture.GR:
        return IBAN_BLOCKS.GR;
      case IBANCulture.HU:
        return IBAN_BLOCKS.HU;
      case IBANCulture.IE:
        return IBAN_BLOCKS.IE;
      case IBANCulture.IS:
        return IBAN_BLOCKS.IS;
      case IBANCulture.IT:
        return IBAN_BLOCKS.IT;
      case IBANCulture.LI:
        return IBAN_BLOCKS.LI;
      case IBANCulture.LT:
        return IBAN_BLOCKS.LT;
      case IBANCulture.LU:
        return IBAN_BLOCKS.LU;
      case IBANCulture.MC:
        return IBAN_BLOCKS.MC;
      case IBANCulture.MT:
        return IBAN_BLOCKS.MT;
      case IBANCulture.NL:
        return IBAN_BLOCKS.NL;
      case IBANCulture.NO:
        return IBAN_BLOCKS.NO;
      case IBANCulture.PL:
        return IBAN_BLOCKS.PL;
      case IBANCulture.PT:
        return IBAN_BLOCKS.PT;
      case IBANCulture.RO:
        return IBAN_BLOCKS.RO;
      case IBANCulture.SE:
        return IBAN_BLOCKS.SE;
      case IBANCulture.SI:
        return IBAN_BLOCKS.SI;
      case IBANCulture.SK:
        return IBAN_BLOCKS.SK;
    }
  }

  private calculateIbanCheckDigits(iban: string): string {
    // Clean up: remove spaces and make uppercase
    iban = iban.replace(/\s+/g, '').toUpperCase();

    // Replace check digits with '00' temporarily
    const countryCode = iban.slice(0, 2);
    const rest = iban.slice(4); // skip the original check digits
    const rearranged = rest + countryCode + '00';

    // Replace letters with numbers: A=10, ..., Z=35
    const numericIban = rearranged
      .split('')
      .map((ch) => {
        return /[A-Z]/.test(ch) ? (ch.charCodeAt(0) - 55).toString() : ch;
      })
      .join('');

    // Calculate remainder
    const remainder = BigInt(numericIban) % 97n;

    // Check digits = 98 - remainder
    const checkDigits = 98n - remainder;
    return checkDigits.toString().padStart(2, '0');
  }

  /**
   * Get the bank code from the iban
   * @param cleaned Cleand iban string
   * @returns bank code
   */
  private getBankCode(cleaned: string): string {
    const culture: IBANCulture =
      IBANCulture[this._countryCode as keyof typeof IBANCulture];

    switch (culture) {
      case IBANCulture.AT:
        return cleaned.substring(4, 9);
      case IBANCulture.BE:
        return cleaned.substring(4, 7);
      case IBANCulture.BG:
        return cleaned.substring(4, 8);
      case IBANCulture.CH:
        return cleaned.substring(4, 9);
      case IBANCulture.CY:
        return cleaned.substring(4, 7);
      case IBANCulture.CZ:
        return cleaned.substring(4, 8);
      case IBANCulture.DE:
        return cleaned.substring(4, 12);
      case IBANCulture.DK:
        return cleaned.substring(4, 8);
      case IBANCulture.EE:
        return cleaned.substring(4, 6);
      case IBANCulture.ES:
        return cleaned.substring(4, 8);
      case IBANCulture.FI:
        return cleaned.substring(4, 10);
      case IBANCulture.FR:
        return cleaned.substring(4, 9);
      case IBANCulture.GB:
        return cleaned.substring(4, 8);
      case IBANCulture.GR:
        return cleaned.substring(4, 7);
      case IBANCulture.HU:
        return cleaned.substring(4, 7);
      case IBANCulture.IE:
        return cleaned.substring(4, 8);
      case IBANCulture.IS:
        return cleaned.substring(4, 8);
      case IBANCulture.IT:
        return cleaned.substring(5, 10);
      case IBANCulture.LI:
        return cleaned.substring(4, 9);
      case IBANCulture.LT:
        return cleaned.substring(4, 9);
      case IBANCulture.LU:
        return cleaned.substring(4, 7);
      case IBANCulture.MC:
        return cleaned.substring(4, 9);
      case IBANCulture.MT:
        return cleaned.substring(4, 9);
      case IBANCulture.NL:
        return cleaned.substring(4, 8);
      case IBANCulture.NO:
        return cleaned.substring(4, 8);
      case IBANCulture.PL:
        return cleaned.substring(4, 7);
      case IBANCulture.PT:
        return cleaned.substring(4, 8);
      case IBANCulture.RO:
        return cleaned.substring(4, 8);
      case IBANCulture.SE:
        return cleaned.substring(4, 7);
      case IBANCulture.SI:
        return cleaned.substring(4, 6);
      case IBANCulture.SK:
        return cleaned.substring(4, 8);
    }
  }

  /**
   * Get the account number of the iban
   * @param cleaned Normalized iban string
   * @returns account number
   */
  private getAccountNumber(cleaned: string): string {
    const culture: IBANCulture =
      IBANCulture[this._countryCode as keyof typeof IBANCulture];

    switch (culture) {
      case IBANCulture.AT:
        return cleaned.substring(9, 20);
      case IBANCulture.BE:
        return cleaned.substring(7, 14);
      case IBANCulture.BG:
        return cleaned.substring(14, 22);
      case IBANCulture.CH:
        return cleaned.substring(9, 21);
      case IBANCulture.CY:
        return cleaned.substring(12, 28);
      case IBANCulture.CZ:
        return cleaned.substring(8, 24);
      case IBANCulture.DE:
        return cleaned.substring(12, 22);
      case IBANCulture.DK:
        return cleaned.substring(8, 18);
      case IBANCulture.EE:
        return cleaned.substring(8, 19);
      case IBANCulture.ES:
        return cleaned.substring(14, 24);
      case IBANCulture.FI:
        return cleaned.substring(10, 17);
      case IBANCulture.FR:
        return cleaned.substring(14, 25);
      case IBANCulture.GB:
        return cleaned.substring(14, 22);
      case IBANCulture.GR:
        return cleaned.substring(11, 27);
      case IBANCulture.HU:
        return cleaned.substring(11, 27);
      case IBANCulture.IE:
        return cleaned.substring(14, 22);
      case IBANCulture.IS:
        return cleaned.substring(16, 26);
      case IBANCulture.IT:
        return cleaned.substring(15, 27);
      case IBANCulture.LI:
        return cleaned.substring(9, 21);
      case IBANCulture.LT:
        return cleaned.substring(9, 20);
      case IBANCulture.LU:
        return cleaned.substring(7, 20);
      case IBANCulture.MC:
        return cleaned.substring(14, 25);
      case IBANCulture.MT:
        return cleaned.substring(13, 31);
      case IBANCulture.NL:
        return cleaned.substring(8, 18);
      case IBANCulture.NO:
        return cleaned.substring(8, 14);
      case IBANCulture.PL:
        return cleaned.substring(12, 28);
      case IBANCulture.PT:
        return cleaned.substring(12, 23);
      case IBANCulture.RO:
        return cleaned.substring(8, 24);
      case IBANCulture.SE:
        return cleaned.substring(7, 24);
      case IBANCulture.SI:
        return cleaned.substring(9, 17);
      case IBANCulture.SK:
        return cleaned.substring(8, 24);
    }
  }

  /**
   * Get the bank name
   * @returns bank name
   */
  private getBankName(): string {
    const culture: IBANCulture =
      IBANCulture[this._countryCode as keyof typeof IBANCulture];

    switch (culture) {
      case IBANCulture.AT:
        return '';
      case IBANCulture.BE:
        return '';
      case IBANCulture.BG:
        return '';
      case IBANCulture.CH:
        return '';
      case IBANCulture.CY:
        return '';
      case IBANCulture.CZ:
        return '';
      case IBANCulture.DE:
        try {
          const bank: BankDe = JSON.parse(
            JSON.stringify(de[Number(this._bankCode)]),
          );

          if (bank) {
            if (!!bank.branches && bank.branches.length > 0)
              return bank.branches[0].name ?? '';
            else if (bank.name) return bank.name ?? '';
          }
        } catch (reason: unknown) {
          console.log(reason);
        }
        return '';
      case IBANCulture.DK:
        return '';
      case IBANCulture.EE:
        return '';
      case IBANCulture.ES:
        return '';
      case IBANCulture.FI:
        return '';
      case IBANCulture.FR:
        return '';
      case IBANCulture.GB:
        return '';
      case IBANCulture.GR:
        return '';
      case IBANCulture.HU:
        return '';
      case IBANCulture.IE:
        return '';
      case IBANCulture.IS:
        return '';
      case IBANCulture.IT:
        return '';
      case IBANCulture.LI:
        return '';
      case IBANCulture.LT:
        return '';
      case IBANCulture.LU:
        return '';
      case IBANCulture.MC:
        return '';
      case IBANCulture.MT:
        return '';
      case IBANCulture.NL:
        return '';
      case IBANCulture.NO:
        return '';
      case IBANCulture.PL:
        return '';
      case IBANCulture.PT:
        return '';
      case IBANCulture.RO:
        return '';
      case IBANCulture.SE:
        return '';
      case IBANCulture.SI:
        return '';
      case IBANCulture.SK:
        return '';
    }
  }

  /**
   * BIC / SWIFT Code
   * @returns swift code
   */
  private getSWIFTCode(): string {
    const culture: IBANCulture =
      IBANCulture[this._countryCode as keyof typeof IBANCulture];

    switch (culture) {
      case IBANCulture.AT:
        return '';
      case IBANCulture.BE:
        return '';
      case IBANCulture.BG:
        return '';
      case IBANCulture.CH:
        return '';
      case IBANCulture.CY:
        return '';
      case IBANCulture.CZ:
        return '';
      case IBANCulture.DE:
        try {
          const bank: BankDe = JSON.parse(
            JSON.stringify(de[Number(this._bankCode)]),
          );

          if (bank) {
            if (bank.branches && bank.branches.length > 0)
              return bank.branches[0].bic ?? '';
            else if (bank.bic) return bank.bic ?? '';
          }
        } catch (reason: unknown) {
          console.log(reason);
        }
        return '';
      case IBANCulture.DK:
        return '';
      case IBANCulture.EE:
        return '';
      case IBANCulture.ES:
        return '';
      case IBANCulture.FI:
        return '';
      case IBANCulture.FR:
        return '';
      case IBANCulture.GB:
        return '';
      case IBANCulture.GR:
        return '';
      case IBANCulture.HU:
        return '';
      case IBANCulture.IE:
        return '';
      case IBANCulture.IS:
        return '';
      case IBANCulture.IT:
        return '';
      case IBANCulture.LI:
        return '';
      case IBANCulture.LT:
        return '';
      case IBANCulture.LU:
        return '';
      case IBANCulture.MC:
        return '';
      case IBANCulture.MT:
        return '';
      case IBANCulture.NL:
        return '';
      case IBANCulture.NO:
        return '';
      case IBANCulture.PL:
        return '';
      case IBANCulture.PT:
        return '';
      case IBANCulture.RO:
        return '';
      case IBANCulture.SE:
        return '';
      case IBANCulture.SI:
        return '';
      case IBANCulture.SK:
        return '';
    }
  }
}
